import { Button, Modal } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlineExport } from 'react-icons/ai';
import { BiShoppingBag } from 'react-icons/bi';
import { GrClose } from 'react-icons/gr';
import { Quill } from 'react-quill';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import UploadDragger from 'src/components/FileUpload/UploadDragger';
import { RootState } from 'src/store';
import { UploadFileApi, UserResponse } from 'src/types';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import { formatMinutesToHoursAndMinutes } from 'src/utils/stringHelpers';

// Constants
const WIZARD_STEPS = {
  PLAN_SELECTION: 1,
  PLAN_DESCRIPTION: 2,
  PLAN_DATE_CHANGING: 3,
  PLAN_CHANGE_SUCCESSFUL: 4,
} as const;

const PLANS = [
  {
    id: 1,
    title: 'Starter',
    amount: 960,
    mostPopular: false,
    color: '#ff8af3',
    hours: 16,
  },
  {
    id: 2,
    title: 'Premium',
    amount: 1980,
    mostPopular: true,
    color: '#7482e3',
    hours: 33,
  },
  {
    id: 3,
    title: 'Enterprise',
    amount: 3000,
    mostPopular: false,
    color: '#44d62c',
    hours: 50,
  },
] as const;

const CUSTOM_PLAN_CONFIG = {
  id: 0,
  title: 'Custom',
  color: '#70d2f0',
  description: 'Build your plan',
  subtitle: 'As much as you need!',
} as const;

const FORM_CONFIG = {
  id: 16,
  name: 'Other',
  imageUrl: 'others.png',
  icon: 'Cyclone',
  description: "Can't find what you need? Let us know how we can help.",
  checkout: false,
  tags: [
    'Custom Request',
    'Bespoke solutions',
    'Implementation Fixes',
    'Support Requests',
    'Maintenance & Updates',
  ],
} as const;

// Types
interface NewPlanData {
  planId: number | null;
  type: 'Upgrade' | 'Downgrade' | null;
  dateOfChange: 'today' | 'next_billing_cycle' | null;
  requirements: string;
  files: string[];
}

interface WizardUpgradeModalProps {
  projectUuid: string;
  isOpen: boolean;
  onClose: () => void;
  latestRefill: number | null;
}

interface PlanSelectionProps {
  latestRefill: number | null;
  setNewPlanData: React.Dispatch<React.SetStateAction<NewPlanData>>;
  setCurrentStep: (step: WizardStep) => void;
}

interface PlanChangeSuccessfulProps {
  projectUuid: string;
  newPlanData: NewPlanData;
  onClose: () => void;
}

interface PlanDateChangingProps {
  setNewPlanData: React.Dispatch<React.SetStateAction<NewPlanData>>;
  setCurrentStep: (step: WizardStep) => void;
}

interface PlanDescriptionProps {
  setNewPlanData: React.Dispatch<React.SetStateAction<NewPlanData>>;
  setCurrentStep: (step: WizardStep) => void;
}

type PlanType = 'Upgrade' | 'Downgrade';
type DateOfChange = 'today' | 'next_billing_cycle';
type WizardStep = (typeof WIZARD_STEPS)[keyof typeof WIZARD_STEPS];

// Utility functions
const calculateHours = (amount: number): number => Math.floor(amount / 60);

const buildFinalDescription = (text: string): string => {
  try {
    const container = document.createElement('div');
    const quill = new Quill(container);

    quill.setText(text);

    const htmlString = container.querySelector('.ql-editor')?.innerHTML || '';

    const finalObj = {
      ...FORM_CONFIG,
      form: [
        {
          name: 'primary_objective_email',
          label: 'What can we help you with?',
          type: 'textEditor',
          answer: htmlString,
        },
      ],
    };

    return JSON.stringify(finalObj);
  } catch (error) {
    console.error('Error building final description:', error);
    return JSON.stringify({ error: 'Failed to build description' });
  }
};

const getNextDay = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  while (tomorrow.getDay() === 6 || tomorrow.getDay() === 0) {
    tomorrow.setDate(tomorrow.getDate() + 1);
  }

  return tomorrow.toISOString().split('T')[0];
};

const determinePlanType = (
  selectedAmount: number,
  currentAmount: number | null
): PlanType => {
  if (currentAmount === null) return 'Upgrade';
  return selectedAmount < currentAmount ? 'Downgrade' : 'Upgrade';
};

// Custom hooks
const usePlanLogic = (latestRefill: number | null) => {
  const isPlanCurrent = useCallback(
    (amount: number): boolean => latestRefill === amount,
    [latestRefill]
  );

  const isCustomCurrent = useMemo(
    (): boolean =>
      latestRefill !== null && !PLANS.some((p) => p.amount === latestRefill),
    [latestRefill]
  );

  const getButtonLabel = useCallback(
    (planAmount: number): string => {
      if (isPlanCurrent(planAmount)) return 'Current plan';
      if (latestRefill !== null && latestRefill > planAmount)
        return 'Downgrade plan';
      return 'Upgrade plan';
    },
    [isPlanCurrent, latestRefill]
  );

  return { isPlanCurrent, isCustomCurrent, getButtonLabel };
};

// Components
function PlanChangeSuccessful({
  projectUuid,
  newPlanData,
  onClose,
}: PlanChangeSuccessfulProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const user = useSelector(
    (store: RootState) => store.auth.user
  ) as UserResponse;

  const createRequest = useCallback(
    async (planData: NewPlanData): Promise<void> => {
      if (!user?.uuid) {
        throw new Error('User not authenticated');
      }

      setIsLoading(true);
      setError(null);

      try {
        const selectedPlan = PLANS.find((plan) => plan.id === planData.planId);
        const planHours = selectedPlan
          ? calculateHours(selectedPlan.amount)
          : 'custom';

        const mountedDescription = `${
          planData.type
        } my plan to ${planHours} hours starting ${
          planData.dateOfChange === 'today'
            ? 'today'
            : 'from the next billing cycle'
        }. ${planData.requirements !== '' ? `\n${planData.requirements}` : ''}`;

        const requestPayload = {
          user_id: user.uuid,
          responsibles: ['03101992-bdf3-11ec-a587-06997b2d6041'],
          project_id: projectUuid,
          target_date: getNextDay(),
          status: 'new',
          title: 'Other',
          subject: `${planData.type} my plan`,
          skip_estimate_approval: planData.type === 'Upgrade',
          uploads: planData.files,
          kickoff_call: null,
          description: buildFinalDescription(mountedDescription),
        };

        await apiRequests.post(apiRoutes.PROJECT_REQUEST, requestPayload);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create request';
        setError(errorMessage);
        console.error('Error creating request:', err);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line
    [projectUuid]
  );

  useEffect(() => {
    if (
      newPlanData.planId !== null &&
      newPlanData.type &&
      newPlanData.dateOfChange
    ) {
      createRequest(newPlanData);
    }
  }, [newPlanData, createRequest]);

  if (error) {
    return (
      <div className="mb-8 flex flex-col items-center justify-center gap-8">
        <h1 className="text-center text-2xl font-bold text-red-600">
          Error processing your request
        </h1>
        <p className="text-center text-gray-600">{error}</p>
        <Button onClick={onClose} type="primary">
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-8 flex flex-col items-center justify-center gap-8">
      <h1 className="text-center text-2xl font-bold">
        {isLoading ? (
          'Processing your request...'
        ) : (
          <>
            Thank you for your submission. The changes will be <br />
            reflected in your account shortly
          </>
        )}
      </h1>

      <Button
        onClick={onClose}
        type="primary"
        loading={isLoading}
        disabled={isLoading}
      >
        Close
      </Button>
    </div>
  );
}

function PlanDateChanging({
  setNewPlanData,
  setCurrentStep,
}: PlanDateChangingProps) {
  const handleDateSelection = useCallback(
    (date: DateOfChange): void => {
      setNewPlanData((prev) => ({
        ...prev,
        dateOfChange: date,
      }));
      setCurrentStep(WIZARD_STEPS.PLAN_CHANGE_SUCCESSFUL);
    },
    [setNewPlanData, setCurrentStep]
  );

  const dateOptions = [
    {
      key: 'today' as const,
      title: 'Today',
      description: (
        <>
          We'll charge your saved payment method, or <br />
          send you an invoice if none is on file.
        </>
      ),
      className:
        'bg-gradient-to-r from-[#4daa1b] via-[#6BE92D] to-[#C2EC9A] text-white hover:shadow-green-500/30',
    },
    {
      key: 'next_billing_cycle' as const,
      title: 'Next billing cycle',
      description: 'Start the change on our next billing date',
      className: 'hover:shadow-2xl',
    },
  ];

  return (
    <div className="mb-8 flex flex-col items-center justify-center gap-8">
      <h1 className="text-2xl font-bold">When would we make the change?</h1>

      <div className="flex flex-col gap-4">
        {dateOptions.map((option) => (
          <button
            key={option.key}
            onClick={() => handleDateSelection(option.key)}
            className={`flex w-[500px] cursor-pointer flex-col rounded-3xl border px-6 py-4 text-center transition-all duration-300 hover:scale-105 ${option.className}`}
            aria-label={`Select ${option.title}`}
          >
            <p className="text-lg font-bold">{option.title}</p>
            <span className="text-sm opacity-90">{option.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PlanDescription({
  setNewPlanData,
  setCurrentStep,
}: PlanDescriptionProps) {
  const [requirements, setRequirements] = useState('');
  const [uploads, setUploads] = useState<UploadFileApi[]>([]);
  const [files, setFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleRequirementsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      const value = e.target.value;
      setRequirements(value);
      setNewPlanData((prev) => ({
        ...prev,
        requirements: value,
      }));
    },
    [setNewPlanData]
  );

  const handleSubmit = useCallback((): void => {
    setNewPlanData((prev) => ({
      ...prev,
      requirements,
      files,
    }));
    setCurrentStep(WIZARD_STEPS.PLAN_DATE_CHANGING);
  }, [requirements, files, setNewPlanData, setCurrentStep]);

  useEffect(() => {
    if (uploads.length > 0) {
      const uploadUrls = uploads.map((item) => item.response?.uuid) as string[];
      setFiles(uploadUrls);
    }
  }, [uploads]);

  return (
    <div className="w-full space-y-6">
      <TextArea
        id="requirements"
        rows={4}
        value={requirements}
        onChange={handleRequirementsChange}
        placeholder="Share detailed requirements so we can estimate hours and plan the best approach."
        maxLength={1000}
        className="w-full"
      />

      <UploadDragger
        setLoading={setLoading}
        uploads={uploads}
        setUploads={setUploads}
        showDownloadIcon={false}
        showRemoveIcon={true}
      />

      <div className="flex w-full items-center justify-center pt-4">
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!requirements.trim() || loading}
        >
          Submit
        </Button>
      </div>
    </div>
  );
}

function PlanSelection({
  latestRefill,
  setNewPlanData,
  setCurrentStep,
}: PlanSelectionProps) {
  const { isPlanCurrent, isCustomCurrent, getButtonLabel } =
    usePlanLogic(latestRefill);

  const handlePlanSelection = useCallback(
    (planId: number): void => {
      const selectedPlan = PLANS.find((p) => p.id === planId);
      const selectedAmount = selectedPlan?.amount ?? latestRefill ?? 0;
      const type = determinePlanType(selectedAmount, latestRefill);

      setNewPlanData((prev) => ({
        ...prev,
        planId,
        type,
      }));

      setCurrentStep(
        planId === CUSTOM_PLAN_CONFIG.id
          ? WIZARD_STEPS.PLAN_DESCRIPTION
          : WIZARD_STEPS.PLAN_DATE_CHANGING
      );
    },
    [latestRefill, setNewPlanData, setCurrentStep]
  );

  const renderPlanCard = useCallback(
    (plan: (typeof PLANS)[number]) => {
      const current = isPlanCurrent(plan.amount);
      const buttonLabel = getButtonLabel(plan.amount);

      const buttonClasses = `
      mb-4 rounded-2xl border px-4 py-2 text-lg font-medium transition-all duration-300 whitespace-nowrap 
      ${
        current
          ? 'cursor-not-allowed opacity-50'
          : 'hover:scale-105 hover:shadow-2xl'
      }
      ${
        plan.mostPopular && !current
          ? 'bg-gradient-to-r from-[#4daa1b] via-[#6BE92D] to-[#b5e786] text-white border-none'
          : 'border-gray-300 hover:border-gray-400'
      }
    `.trim();

      return (
        <div
          key={plan.id}
          className="relative flex h-auto min-h-[250px] flex-col items-center justify-between gap-6 rounded-3xl border p-3"
          style={{ borderColor: plan.color }}
        >
          {plan.mostPopular && (
            <div
              className="absolute left-1/2 top-0 flex h-8 -translate-x-1/2 items-center justify-center whitespace-nowrap rounded-b-xl px-6 font-medium text-white"
              style={{ backgroundColor: plan.color }}
            >
              Most Popular
            </div>
          )}
          <div />

          <div className="mt-8 flex w-full flex-col items-center gap-4">
            <h2 className="w-full text-left text-2xl font-medium text-gray-800">
              {plan.title}
            </h2>
            <p className="text-4xl font-bold" style={{ color: plan.color }}>
              {plan.hours} hours
            </p>
            <span className="w-full text-left text-gray-500">Per month</span>
          </div>

          <button
            type="button"
            disabled={current}
            onClick={() => !current && handlePlanSelection(plan.id)}
            className={buttonClasses}
            aria-label={`${buttonLabel} - ${plan.title}`}
          >
            {buttonLabel}
          </button>
        </div>
      );
    },
    [isPlanCurrent, getButtonLabel, handlePlanSelection]
  );

  return (
    <div className="my-10 grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {PLANS.map(renderPlanCard)}

      {/* Custom Plan Card */}
      <div className="relative flex h-auto min-h-[250px] flex-col items-center justify-between gap-6 rounded-3xl border border-[#70d2f0] p-3">
        <div />
        <div className="mt-8 flex w-full flex-col items-center gap-4">
          <h2 className="w-full text-left text-2xl font-medium text-gray-800">
            {CUSTOM_PLAN_CONFIG.title}
          </h2>
          <p
            className={`whitespace-nowrap font-bold text-[#70d2f0] ${
              latestRefill && !PLANS.some((p) => p.amount === latestRefill)
                ? 'text-4xl'
                : 'text-2xl'
            }`}
          >
            {latestRefill
              ? PLANS.some((p) => p.amount === latestRefill)
                ? CUSTOM_PLAN_CONFIG.description
                : formatMinutesToHoursAndMinutes(latestRefill)
              : CUSTOM_PLAN_CONFIG.description}
          </p>
          <span className="w-full text-left text-gray-500">
            {CUSTOM_PLAN_CONFIG.subtitle}
          </span>
        </div>

        <button
          type="button"
          disabled={isCustomCurrent}
          onClick={() =>
            !isCustomCurrent && handlePlanSelection(CUSTOM_PLAN_CONFIG.id)
          }
          className={`
            mb-4 rounded-2xl border px-4 py-2 text-lg font-medium transition-all duration-300
            ${
              isCustomCurrent
                ? 'cursor-not-allowed opacity-50'
                : 'border-gray-300 hover:scale-105 hover:border-gray-400 hover:shadow-2xl'
            }
          `.trim()}
          aria-label={`Select custom plan${
            latestRefill
              ? ` - ${formatMinutesToHoursAndMinutes(latestRefill)}`
              : ''
          }`}
        >
          {isCustomCurrent ? 'Current plan' : 'Select custom'}
        </button>
      </div>
    </div>
  );
}

function WizardUpgradeModal({
  projectUuid,
  isOpen,
  onClose,
  latestRefill,
}: WizardUpgradeModalProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>(
    WIZARD_STEPS.PLAN_SELECTION
  );
  const [newPlanData, setNewPlanData] = useState<NewPlanData>({
    type: null,
    planId: null,
    dateOfChange: null,
    requirements: '',
    files: [],
  });

  // Reset state when modal opens
  useEffect(() => {
    setCurrentStep(WIZARD_STEPS.PLAN_SELECTION);

    if (isOpen) {
      setNewPlanData({
        type: null,
        planId: null,
        dateOfChange: null,
        requirements: '',
        files: [],
      });
    }
  }, [isOpen]);

  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  const handleBackToSelection = useCallback((): void => {
    setCurrentStep(WIZARD_STEPS.PLAN_SELECTION);
  }, []);

  if (!isOpen) return null;

  const renderHeader = () => {
    if (currentStep === WIZARD_STEPS.PLAN_DESCRIPTION) {
      return (
        <>
          <h1 className="font-medium text-gray-800">
            What are you looking to do?
          </h1>
          <button
            onClick={handleBackToSelection}
            className="ml-auto rounded p-1 transition-colors hover:bg-gray-100"
            aria-label="Go back to plan selection"
          >
            <GrClose size={16} />
          </button>
        </>
      );
    }

    return (
      <>
        <div className="flex items-center gap-2">
          <BiShoppingBag size={18} className="text-blue-600" />
          <h1 className="text-xl font-medium text-blue-600">Modify my plan</h1>
        </div>
        <div className="ml-auto flex cursor-pointer items-center gap-2 transition-colors hover:text-blue-800">
          <Link
            to="https://calendar.app.google/g7fxpk693UYS5pPx7"
            target="_blank"
            className="underline-hover-link font-medium text-blue-600"
          >
            Contact sales
          </Link>
          <AiOutlineExport size={14} className="text-blue-600" />
        </div>
        <button
          onClick={handleClose}
          className="ml-4 rounded p-1 transition-colors hover:bg-gray-100"
          aria-label="Go back to plan selection"
        >
          <GrClose size={16} />
        </button>
      </>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case WIZARD_STEPS.PLAN_SELECTION:
        return (
          <PlanSelection
            setNewPlanData={setNewPlanData}
            setCurrentStep={setCurrentStep}
            latestRefill={latestRefill}
          />
        );
      case WIZARD_STEPS.PLAN_DESCRIPTION:
        return (
          <PlanDescription
            setNewPlanData={setNewPlanData}
            setCurrentStep={setCurrentStep}
          />
        );
      case WIZARD_STEPS.PLAN_DATE_CHANGING:
        return (
          <PlanDateChanging
            setNewPlanData={setNewPlanData}
            setCurrentStep={setCurrentStep}
          />
        );
      case WIZARD_STEPS.PLAN_CHANGE_SUCCESSFUL:
        return (
          <PlanChangeSuccessful
            projectUuid={projectUuid}
            newPlanData={newPlanData}
            onClose={handleClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      className="!m-4 w-auto lg:min-w-[1000px]"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      closable={false}
      destroyOnClose
      centered
    >
      <div className="flex w-full items-center gap-4">{renderHeader()}</div>

      <div className="my-2 flex items-center justify-center">
        {renderStepContent()}
      </div>
    </Modal>
  );
}

export default WizardUpgradeModal;
